import { supabase } from './supabaseClient';
const profiles_table_name = 'ml_profiles_staging';

/**
 * Insert a new user profile
 * @param {string} userId - Supabase user ID
 * @param {Object} profileData - Profile fields to store
 * @returns {Promise<Object>} - { data, error }
 */
export const createUserProfile = async (userId, profileData) => {
  const payload = { id: userId, ...profileData };

  const { data, error } = await supabase
    .from(profiles_table_name)
    .insert(payload)
    .select()
    .maybeSingle();

  return { data, error };
};

/**
 * Update user profile data
 * @param {string} userId - Supabase user ID
 * @param {Object} profileData - Profile fields to update
 * @returns {Promise<Object>} - { data, error }
 */
export const updateUserProfile = async (userId, profileData) => {
  const payload = { ...profileData };

  const { data, error } = await supabase
    .from(profiles_table_name)
    .update(payload)
    .eq('id', userId)
    .select()
    .maybeSingle();
  return { data, error };
};

/**
 * Fetch the profile for a given user ID
 * @param {string} userId - Supabase user ID
 * @returns {Promise<Object>} - { data, error }
 */
export const fetchUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from(profiles_table_name)
    .select('*')
    .eq('id', userId)
    .maybeSingle(); // Handles 0 or 1 row
  return { data, error };
};

/**
 * Get or create a user profile
 * If profile does not exist, creates it with profileData
 * @param {string} userId - Supabase user ID
 * @param {Object} profileData - Optional default fields
 * @returns {Promise<Object>} - { data, error }
 */
export const getOrCreateProfile = async (userId, profileData = {}) => {
  const { data, error } = await fetchUserProfile(userId);

  if (!data && !error) {
    // Get user email from auth session
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const userEmail = user?.email;
    const username = userEmail ? userEmail.split('@')[0] : null;

    // Create profile with email and username
    return await createUserProfile(userId, {
      username,
      email: userEmail,
      full_name: null,
      ...profileData,
    });
  }

  // If profile exists but missing username/email, update it
  if (data && (!data.username || !data.email)) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const userEmail = user?.email;
    const username = userEmail ? userEmail.split('@')[0] : null;

    await updateUserProfile(userId, {
      username: data.username || username,
      email: data.email || userEmail,
    });

    // Refetch the updated profile
    return await fetchUserProfile(userId);
  }

  return { data, error };
};

/**
 * Fetch user's custom reflection questions
 * @param {string} userId - Supabase user ID
 * @returns {Promise<Object>} - { data: questions array, error }
 */
export const fetchUserReflectionQuestions = async (userId) => {
  const { data, error } = await supabase
    .from(profiles_table_name)
    .select('reflection_questions')
    .eq('id', userId)
    .maybeSingle();

  if (error) return { data: null, error };

  return { data: data?.reflection_questions || null, error: null };
};

/**
 * Update user's custom reflection questions
 * @param {string} userId - Supabase user ID
 * @param {Array} questions - Array of question objects
 * @returns {Promise<Object>} - { data, error }
 */
export const updateUserReflectionQuestions = async (userId, questions) => {
  const { data, error } = await supabase
    .from(profiles_table_name)
    .update({ reflection_questions: questions })
    .eq('id', userId)
    .select()
    .maybeSingle();

  return { data, error };
};

/**
 * Fetch user's agent persona preferences
 * @param {string} userId - Supabase user ID
 * @returns {Promise<Object>} - { data: persona preferences object, error }
 */
export const fetchUserAgentPersonas = async (userId) => {
  const { data, error } = await supabase
    .from(profiles_table_name)
    .select('agent_personas')
    .eq('id', userId)
    .maybeSingle();

  if (error) return { data: null, error };

  return { data: data?.agent_personas || null, error: null };
};

/**
 * Update user's agent persona preferences
 * @param {string} userId - Supabase user ID
 * @param {Object} personas - Object with agent persona preferences
 * @returns {Promise<Object>} - { data, error }
 */
export const updateUserAgentPersonas = async (userId, personas) => {
  const { data, error } = await supabase
    .from(profiles_table_name)
    .update({ agent_personas: personas })
    .eq('id', userId)
    .select()
    .maybeSingle();

  return { data, error };
};
